import React from 'react';
import * as _ from 'underscore';

import DataConnector from 'connect/DataConnector';
import {Registry} from 'constants/RegistryConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import NavStore from 'nav/NavStore';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {getInitialRegistry, setDefaultRegistry} from 'registry/DefaultRegistry';
import SelectDropdown, {SelectDropdownErrorStyle} from 'ui-library/dropdowns/SelectDropdown';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

class RegistrySelectorConnector extends DataConnector<Props, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [NavStore];

  enhanceProps(): EnhancedProps {
    return {registries: this.navStore().getRegistries()};
  }
}

interface Props {
  disabled?: boolean;
  disabledTooltip?: string;
  onChange: (e: {target: {value: Registry | null}}) => void;
  registryId?: string | null;
}
interface EnhancedProps {
  registries: Array<Registry>;
}

class RegistrySelector extends React.Component<Props & EnhancedProps> {
  static defaultProps = {
    disabled: false,
  };

  override componentDidMount(): void {
    FileBrowserActions.fetchSources();
    this.setInitialRegistry();
  }

  override componentDidUpdate(prevProps: Props & EnhancedProps): void {
    if (this.props.registryId) {
      setDefaultRegistry(this.props.registryId);
    }
    if (prevProps.registries !== this.props.registries) {
      this.setInitialRegistry();
    }
  }

  private setInitialRegistry = (): void => {
    if (!this.props.registryId && this.props.registries && this.props.registries.length >= 1) {
      this.props.onChange({target: {value: getInitialRegistry(this.props.registries)}});
    }
  };

  private getRegistryDisplayName = (registry?: Registry): string | null =>
    registry ? `${getPartyDisplayString(registry.s_owner)} / ${registry.name}` : null;

  private getRegistryOptions = (): Array<{value: string; name: string}> => {
    const options = this.props.registries.map((registry) => ({
      value: registry.id,
      name: this.getRegistryDisplayName(registry)!,
    }));
    return _.sortBy(options, 'name');
  };

  private handleRegistrySelect = (id: string): void => {
    const registry = this.props.registries.find((registry) => registry.id === id)!;
    this.props.onChange({target: {value: registry}});
  };

  override render(): JSX.Element {
    if (this.props.registries.length === 0) {
      return <div>There are no registries.</div>;
    } else if (this.props.registries.length === 1) {
      // If there is only one registry option, render as text.
      return <div>{this.props.registries[0].name}</div>;
    } else {
      const currentDisplay =
        this.getRegistryDisplayName(
          this.props.registries.find((registry) => registry.id === this.props.registryId)
        ) ?? 'Select a registry';
      return (
        <SelectDropdown
          disabled={this.props.disabled}
          displayName={currentDisplay}
          errorStyle={SelectDropdownErrorStyle.TOOLTIP}
          error={this.props.disabled ? (this.props.disabledTooltip ?? null) : null}
          menu={
            <SearchableMenu
              selectedValue={this.props.registryId}
              width={Menu.WIDTHS.FULL}
              optionDatas={this.getRegistryOptions()}
              onSelect={this.handleRegistrySelect}
            />
          }
          tooltipToggleDisplayMode={ToggleDisplayMode.BLOCK}
        />
      );
    }
  }
}

export default RegistrySelectorConnector.connect(RegistrySelector);
